import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft,
    HeartPulse,
    Save,
    Plus,
    X,
    Wind,
    Flame,
    Waves,
    Leaf,
    Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { describeCombo } from "@/constants/dosha";
import { TID } from "@/constants/testIds";

const ELEMENT_ICON = { vata: Wind, pitta: Flame, kapha: Waves };

const LEVELS = [
    { value: "very_low", label: "Very low" },
    { value: "low", label: "Low" },
    { value: "moderate", label: "Moderate" },
    { value: "high", label: "High" },
    { value: "very_high", label: "Very high" },
];

const BOWEL = [
    { value: "regular", label: "Regular" },
    { value: "irregular", label: "Irregular" },
    { value: "constipated", label: "Constipated" },
    { value: "loose", label: "Loose" },
    { value: "alternating", label: "Alternating" },
];

const URINATION = [
    { value: "normal", label: "Normal" },
    { value: "frequent", label: "Frequent" },
    { value: "infrequent", label: "Infrequent" },
    { value: "burning", label: "Burning" },
    { value: "painful", label: "Painful" },
];

const EXERCISE = [
    { value: "none", label: "None" },
    { value: "light", label: "Light" },
    { value: "moderate", label: "Moderate" },
    { value: "intense", label: "Intense" },
    { value: "daily_yoga", label: "Daily yoga" },
];

const DIET = [
    { value: "vegetarian", label: "Vegetarian" },
    { value: "vegan", label: "Vegan" },
    { value: "eggetarian", label: "Eggetarian" },
    { value: "non_vegetarian", label: "Non-vegetarian" },
    { value: "jain", label: "Jain" },
    { value: "sattvic", label: "Sattvic" },
    { value: "other", label: "Other" },
];

const PREGNANCY = [
    { value: "not_applicable", label: "N/A" },
    { value: "no", label: "No" },
    { value: "yes", label: "Yes" },
    { value: "trying", label: "Trying" },
    { value: "postpartum", label: "Postpartum" },
];

const HABIT = [
    { value: "never", label: "Never" },
    { value: "occasional", label: "Occasional" },
    { value: "regular", label: "Regular" },
    { value: "former", label: "Former" },
];

function Field({ label, hint, children }) {
    return (
        <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                {label}
            </span>
            <div className="mt-1.5">{children}</div>
            {hint && (
                <p className="mt-1 text-[10px] text-veda-muted">{hint}</p>
            )}
        </label>
    );
}

function SegPicker({ options, value, onChange, testKey }) {
    return (
        <div className="flex flex-wrap gap-1.5">
            {options.map((o) => (
                <button
                    key={o.value}
                    type="button"
                    data-testid={TID.healthProfileField(`${testKey}-${o.value}`)}
                    onClick={() => onChange(value === o.value ? null : o.value)}
                    className={`border px-3 py-1.5 text-xs font-medium transition ${
                        value === o.value
                            ? "border-veda-brand bg-veda-brand text-white"
                            : "border-veda-border bg-veda-surface text-veda-onSurface hover:border-veda-brand/60"
                    }`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

function ChipListInput({ items, onChange, placeholder, testKey }) {
    const [text, setText] = useState("");
    function add() {
        const v = text.trim();
        if (!v) return;
        if (items.map((i) => i.toLowerCase()).includes(v.toLowerCase())) {
            setText("");
            return;
        }
        onChange([...items, v]);
        setText("");
    }
    function remove(idx) {
        onChange(items.filter((_, i) => i !== idx));
    }
    return (
        <div>
            <div className="flex gap-1.5">
                <input
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            add();
                        }
                    }}
                    placeholder={placeholder}
                    data-testid={TID.healthProfileField(`${testKey}-input`)}
                    className="flex-1 border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                />
                <button
                    type="button"
                    onClick={add}
                    data-testid={TID.healthProfileField(`${testKey}-add`)}
                    className="inline-flex items-center gap-1 border border-veda-border bg-veda-surface2 px-3 text-xs font-semibold uppercase tracking-[0.12em] text-veda-onSurface hover:border-veda-brand hover:text-veda-brand"
                >
                    <Plus size={12} /> Add
                </button>
            </div>
            {items.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {items.map((it, idx) => (
                        <span
                            key={`${it}-${idx}`}
                            className="inline-flex items-center gap-1 rounded-pill border border-veda-border bg-veda-surface2 py-0.5 pl-2.5 pr-1 text-[11px] font-semibold text-veda-onSurface"
                        >
                            {it}
                            <button
                                type="button"
                                onClick={() => remove(idx)}
                                data-testid={TID.healthProfileField(`${testKey}-remove-${idx}`)}
                                className="grid h-4 w-4 place-items-center text-veda-muted hover:text-veda-error"
                                aria-label={`Remove ${it}`}
                            >
                                <X size={11} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

const EMPTY = {
    current_imbalance: "",
    primary_complaint: "",
    secondary_complaints: [],
    sleep: null,
    stress: null,
    digestion: null,
    appetite: null,
    bowel: null,
    urination: null,
    energy: null,
    exercise: null,
    food_preference: null,
    allergies: [],
    medical_history: [],
    current_medicines: [],
    previous_treatments: [],
    pregnancy: null,
    smoking: null,
    alcohol: null,
    notes: "",
};

export default function HealthProfile() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dosha, setDosha] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [initial, setInitial] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [hp, dp] = await Promise.all([
                api.get("/patient/health-profile"),
                api.get("/dosha/profile"),
            ]);
            const data = hp.data?.health_profile;
            if (data) {
                setForm({ ...EMPTY, ...data });
                setInitial(data);
            } else {
                setForm(EMPTY);
                setInitial(null);
            }
            setDosha(dp.data?.profile || null);
        } catch {
            toast.error("Could not load your health profile.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                current_imbalance: form.current_imbalance?.trim() || null,
                primary_complaint: form.primary_complaint?.trim() || null,
                notes: form.notes?.trim() || null,
            };
            const { data } = await api.post("/patient/health-profile", payload);
            setInitial(data.health_profile);
            toast.success("Your health profile is updated.");
            navigate("/dashboard");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string" ? detail : "Could not save profile.",
            );
        } finally {
            setSaving(false);
        }
    }

    const info = useMemo(() => (dosha ? describeCombo(dosha.primary) : null), [dosha]);
    const primary = (dosha?.primary || "").split("-")[0];
    const DoshaIcon = ELEMENT_ICON[primary] || Leaf;

    if (loading) {
        return (
            <div className="veda-container py-20 text-center text-sm text-veda-muted">
                Loading your Ayurvedic profile…
            </div>
        );
    }

    return (
        <div
            data-testid={TID.healthProfilePage}
            className="pb-16"
        >
            {/* Header */}
            <div className="border-b border-veda-border">
                <div className="veda-container py-6 md:py-10">
                    <Link
                        to="/dashboard"
                        className="inline-flex items-center gap-1 text-xs font-medium text-veda-muted hover:text-veda-onSurface"
                    >
                        <ArrowLeft size={13} /> Dashboard
                    </Link>
                    <div className="mt-4 flex flex-wrap items-baseline justify-between gap-3">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                                Ayurvedic health profile
                            </p>
                            <h1 className="font-display mt-1 text-3xl text-veda-onSurface md:text-5xl">
                                Your body, in your Vaidya's language.
                            </h1>
                            <p className="mt-2 max-w-xl text-sm text-veda-muted">
                                Update how you're feeling in body and mind. Your
                                Vaidya sees this before every consult, and the
                                AI Guide personalises advice using it.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <form
                onSubmit={submit}
                data-testid={TID.healthProfileForm}
                className="veda-container py-8 md:py-12 space-y-8"
            >
                {/* Prakriti summary (read-only, from dosha quiz) */}
                <section className="border border-veda-border bg-veda-surface2/40 p-5">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="grid h-9 w-9 place-items-center bg-veda-brand/10 text-veda-brand">
                            <DoshaIcon size={15} />
                        </span>
                        <div className="flex-1">
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Prakriti (from dosha quiz)
                            </p>
                            <p className="font-display mt-0.5 text-lg text-veda-onSurface">
                                {dosha ? info?.name : "Not taken yet"}
                            </p>
                        </div>
                        {dosha ? (
                            <div className="flex gap-3 text-center">
                                {["vata", "pitta", "kapha"].map((d) => (
                                    <div key={d}>
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-muted">
                                            {d}
                                        </p>
                                        <p className="font-display text-base text-veda-onSurface">
                                            {dosha.scores?.[d] ?? 0}%
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <Link
                                to="/wellness/quiz"
                                className="inline-flex items-center gap-1 border border-veda-brand bg-veda-brand px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white"
                            >
                                <Sparkles size={11} /> Take quiz
                            </Link>
                        )}
                    </div>
                </section>

                {/* Complaints */}
                <section className="border border-veda-border bg-veda-surface p-5 md:p-6 space-y-5">
                    <div className="flex items-center gap-2">
                        <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                            <HeartPulse size={14} />
                        </span>
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Chief concerns
                            </p>
                            <p className="font-display text-lg text-veda-onSurface">
                                What's bringing you to Ayurveda?
                            </p>
                        </div>
                    </div>
                    <div className="grid gap-5 md:grid-cols-2">
                        <Field
                            label="Current imbalance"
                            hint="How you'd describe what's off, e.g. Vata aggravation, Pitta excess."
                        >
                            <input
                                type="text"
                                value={form.current_imbalance || ""}
                                onChange={(e) =>
                                    set("current_imbalance")(e.target.value)
                                }
                                placeholder="Vata excess / Pitta aggravation…"
                                data-testid={TID.healthProfileField("current-imbalance")}
                                className="w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                            />
                        </Field>
                        <Field
                            label="Primary complaint"
                            hint="The main issue you'd like to address."
                        >
                            <input
                                type="text"
                                value={form.primary_complaint || ""}
                                onChange={(e) =>
                                    set("primary_complaint")(e.target.value)
                                }
                                placeholder="e.g. insomnia, acidity, joint pain…"
                                data-testid={TID.healthProfileField("primary-complaint")}
                                className="w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                            />
                        </Field>
                    </div>
                    <Field label="Secondary complaints" hint="Add any smaller concerns.">
                        <ChipListInput
                            items={form.secondary_complaints}
                            onChange={set("secondary_complaints")}
                            placeholder="Dry skin, low energy, headaches…"
                            testKey="secondary-complaints"
                        />
                    </Field>
                </section>

                {/* Lifestyle scales */}
                <section className="border border-veda-border bg-veda-surface p-5 md:p-6 space-y-5">
                    <div className="flex items-center gap-2">
                        <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                            <Sparkles size={14} />
                        </span>
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Daily patterns
                            </p>
                            <p className="font-display text-lg text-veda-onSurface">
                                How does a typical day feel?
                            </p>
                        </div>
                    </div>
                    <div className="grid gap-5 md:grid-cols-2">
                        <Field label="Sleep">
                            <SegPicker options={LEVELS} value={form.sleep} onChange={set("sleep")} testKey="sleep" />
                        </Field>
                        <Field label="Stress">
                            <SegPicker options={LEVELS} value={form.stress} onChange={set("stress")} testKey="stress" />
                        </Field>
                        <Field label="Digestion (agni)">
                            <SegPicker options={LEVELS} value={form.digestion} onChange={set("digestion")} testKey="digestion" />
                        </Field>
                        <Field label="Appetite">
                            <SegPicker options={LEVELS} value={form.appetite} onChange={set("appetite")} testKey="appetite" />
                        </Field>
                        <Field label="Energy">
                            <SegPicker options={LEVELS} value={form.energy} onChange={set("energy")} testKey="energy" />
                        </Field>
                        <Field label="Exercise">
                            <SegPicker options={EXERCISE} value={form.exercise} onChange={set("exercise")} testKey="exercise" />
                        </Field>
                        <Field label="Bowel">
                            <SegPicker options={BOWEL} value={form.bowel} onChange={set("bowel")} testKey="bowel" />
                        </Field>
                        <Field label="Urination">
                            <SegPicker options={URINATION} value={form.urination} onChange={set("urination")} testKey="urination" />
                        </Field>
                        <Field label="Food preference">
                            <SegPicker options={DIET} value={form.food_preference} onChange={set("food_preference")} testKey="food-preference" />
                        </Field>
                    </div>
                </section>

                {/* Medical */}
                <section className="border border-veda-border bg-veda-surface p-5 md:p-6 space-y-5">
                    <div className="flex items-center gap-2">
                        <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                            <HeartPulse size={14} />
                        </span>
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Medical background
                            </p>
                            <p className="font-display text-lg text-veda-onSurface">
                                So your Vaidya can suggest tests & remedies safely.
                            </p>
                        </div>
                    </div>
                    <div className="grid gap-5 md:grid-cols-2">
                        <Field label="Allergies">
                            <ChipListInput
                                items={form.allergies}
                                onChange={set("allergies")}
                                placeholder="Dust, peanuts, pollen…"
                                testKey="allergies"
                            />
                        </Field>
                        <Field label="Medical history">
                            <ChipListInput
                                items={form.medical_history}
                                onChange={set("medical_history")}
                                placeholder="Diabetes, migraine, thyroid…"
                                testKey="medical-history"
                            />
                        </Field>
                        <Field label="Current medicines & supplements">
                            <ChipListInput
                                items={form.current_medicines}
                                onChange={set("current_medicines")}
                                placeholder="Ashwagandha 500mg, Metformin…"
                                testKey="current-medicines"
                            />
                        </Field>
                        <Field label="Previous treatments">
                            <ChipListInput
                                items={form.previous_treatments}
                                onChange={set("previous_treatments")}
                                placeholder="Panchakarma, yoga therapy, physiotherapy…"
                                testKey="previous-treatments"
                            />
                        </Field>
                    </div>
                </section>

                {/* Personal */}
                <section className="border border-veda-border bg-veda-surface p-5 md:p-6 space-y-5">
                    <div className="flex items-center gap-2">
                        <span className="grid h-8 w-8 place-items-center bg-veda-brand/10 text-veda-brand">
                            <Leaf size={14} />
                        </span>
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Personal
                            </p>
                            <p className="font-display text-lg text-veda-onSurface">
                                Only shared with your Vaidya.
                            </p>
                        </div>
                    </div>
                    <div className="grid gap-5 md:grid-cols-3">
                        <Field label="Pregnancy">
                            <SegPicker options={PREGNANCY} value={form.pregnancy} onChange={set("pregnancy")} testKey="pregnancy" />
                        </Field>
                        <Field label="Smoking">
                            <SegPicker options={HABIT} value={form.smoking} onChange={set("smoking")} testKey="smoking" />
                        </Field>
                        <Field label="Alcohol">
                            <SegPicker options={HABIT} value={form.alcohol} onChange={set("alcohol")} testKey="alcohol" />
                        </Field>
                    </div>
                    <Field label="Anything else your Vaidya should know?">
                        <textarea
                            rows={3}
                            value={form.notes || ""}
                            onChange={(e) => set("notes")(e.target.value)}
                            maxLength={1000}
                            placeholder="Wake at 3am often, dry mouth in mornings…"
                            data-testid={TID.healthProfileField("notes")}
                            className="w-full border border-veda-border bg-veda-surface px-3 py-2 text-sm focus:border-veda-brand focus:outline-none"
                        />
                    </Field>
                </section>

                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-veda-divider pt-6">
                    <p className="text-[11px] text-veda-muted">
                        {initial
                            ? `Last updated ${new Date(initial.updated_at).toLocaleString()}`
                            : "You haven't saved a health profile yet."}
                    </p>
                    <button
                        type="submit"
                        disabled={saving}
                        data-testid={TID.healthProfileSubmit}
                        className="inline-flex items-center gap-2 bg-veda-inverse px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onInverse disabled:opacity-60"
                    >
                        <Save size={12} />
                        {saving ? "Saving…" : initial ? "Update profile" : "Save profile"}
                    </button>
                </div>
            </form>
        </div>
    );
}
